import posthog from 'posthog-js';

const enabledMeta = document.querySelector("meta[name='posthog-enabled']");
const pagePathMeta = document.querySelector("meta[name='posthog-page-path']");
const apiKey = import.meta.env.VITE_POSTHOG_KEY;
const apiHost = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';
const VIEW_DEDUP_PREFIX = 'buzzsol:posthog:viewed:';
const PAGE_EVENTS = ['$pageview', '$pageleave'];

/**
 * PostHog が自動付与する未加工のURL系プロパティ。
 * 実IDを含む値は除外し、ページイベントにはルートテンプレート由来の値だけを付与する。
 *
 * @type {string[]}
 */
const URL_PROPERTY_DENYLIST = [
  '$current_url',
  '$pathname',
  '$host',
  '$referrer',
  '$referring_domain',
  '$initial_current_url',
  '$initial_pathname',
  '$initial_host',
  '$initial_referrer',
  '$initial_referring_domain',
  '$session_entry_url',
  '$session_entry_host',
  '$session_entry_pathname',
  '$session_entry_referrer',
  '$session_entry_referring_domain',
];

/**
 * @param {Record<string, unknown>|undefined|null} properties
 * @returns {Record<string, unknown>|undefined|null}
 */
function stripUrlProperties(properties) {
  if (!properties || typeof properties !== 'object') {
    return properties;
  }

  /** @type {Record<string, unknown>} */
  const sanitized = { ...properties };

  Object.keys(sanitized).forEach((key) => {
    const isDenied =
      URL_PROPERTY_DENYLIST.includes(key) ||
      key.includes('url') ||
      key.includes('pathname') ||
      key.includes('href') ||
      key.includes('referrer') ||
      key.includes('referring_domain') ||
      key === '$host' ||
      key.endsWith('_host');

    if (isDenied) {
      delete sanitized[key];
    }
  });

  return sanitized;
}

/**
 * @param {{ event?: string, properties?: Record<string, unknown>, $set?: Record<string, unknown>, $set_once?: Record<string, unknown> }|null} event
 * @returns {typeof event}
 */
function beforeSend(event) {
  if (!event) {
    return null;
  }

  event.properties = stripUrlProperties(event.properties);
  if (event.$set) {
    event.$set = stripUrlProperties(event.$set);
  }
  if (event.$set_once) {
    event.$set_once = stripUrlProperties(event.$set_once);
  }

  const pagePath = pagePathMeta?.getAttribute('content');
  if (pagePath && event.event && PAGE_EVENTS.includes(event.event)) {
    event.properties = {
      ...event.properties,
      $current_url: `${window.location.origin}${pagePath}`,
      $pathname: pagePath,
      $host: window.location.host,
    };

    if (event.event === '$pageleave') {
      event.properties.$prev_pageview_pathname = pagePath;
    }
  }

  return event;
}

/**
 * @typedef {{ pattern: RegExp, event: string, props?: Record<string, string> }} ViewRule
 * @type {ViewRule[]}
 */
const VIEW_RULES = [
  { pattern: /^\/dashboard\/?$/, event: 'dashboard_viewed' },
  { pattern: /^\/agency\/dashboard\/?$/, event: 'dashboard_viewed' },
  {
    pattern: /^\/analysis\/web(\/|$)/,
    event: 'feature_viewed',
    props: { feature_key: 'web_analysis' },
  },
  {
    pattern: /^\/analysis\/sns-inflow(\/|$)/,
    event: 'feature_viewed',
    props: { feature_key: 'sns_inflow_analysis' },
  },
  {
    pattern: /^\/analysis\/instagram(\/|$)/,
    event: 'feature_viewed',
    props: { feature_key: 'instagram_analysis' },
  },
  {
    pattern: /^\/analysis\/youtube(\/|$)/,
    event: 'feature_viewed',
    props: { feature_key: 'youtube_analysis' },
  },
  {
    pattern: /^\/analysis\/gbp(\/|$)/,
    event: 'feature_viewed',
    props: { feature_key: 'gbp_analysis' },
  },
  {
    pattern: /^\/analysis\/ai-overall(\/|$)/,
    event: 'feature_viewed',
    props: { feature_key: 'ai_overall_analysis' },
  },
  {
    pattern: /^\/analysis\/aio-keyword(\/|$)/,
    event: 'feature_viewed',
    props: { feature_key: 'aio_keyword_analysis' },
  },
  {
    pattern: /^\/analysis\/seo-keyword(\/|$)/,
    event: 'feature_viewed',
    props: { feature_key: 'seo_keyword_analysis' },
  },
  {
    pattern: /^\/ai\/cms(\/|$)/,
    event: 'feature_viewed',
    props: { feature_key: 'ai_cms' },
  },
  {
    pattern: /^\/ai\/instagram(\/|$)/,
    event: 'feature_viewed',
    props: { feature_key: 'ai_instagram' },
  },
  {
    pattern: /^\/support(\/|$)/,
    event: 'feature_viewed',
    props: { feature_key: 'support' },
  },
  {
    pattern: /^\/agency\/users\/cms-content\//,
    event: 'agency_content_viewed',
    props: { content_type: 'cms' },
  },
  {
    pattern: /^\/agency\/users\/instagram-content\//,
    event: 'agency_content_viewed',
    props: { content_type: 'instagram' },
  },
  {
    pattern: /^\/agency\/ai-contents\/cms-content\/[^/]+/,
    event: 'agency_content_viewed',
    props: { content_type: 'cms' },
  },
  {
    pattern: /^\/agency\/ai-contents\/instagram-content\/[^/]+/,
    event: 'agency_content_viewed',
    props: { content_type: 'instagram' },
  },
  {
    pattern: /^\/agency\/users\/[^/]+\/?$/,
    event: 'agency_target_user_viewed',
  },
  {
    pattern: /^\/agency\/analysis\/web(\/|$)/,
    event: 'agency_analysis_viewed',
    props: { feature_key: 'web_analysis' },
  },
  {
    pattern: /^\/agency\/analysis\/sns-inflow(\/|$)/,
    event: 'agency_analysis_viewed',
    props: { feature_key: 'sns_inflow_analysis' },
  },
  {
    pattern: /^\/agency\/analysis\/instagram(\/|$)/,
    event: 'agency_analysis_viewed',
    props: { feature_key: 'instagram_analysis' },
  },
  {
    pattern: /^\/agency\/analysis\/gbp(\/|$)/,
    event: 'agency_analysis_viewed',
    props: { feature_key: 'gbp_analysis' },
  },
  {
    pattern: /^\/agency\/analysis\/aio(\/|$)/,
    event: 'agency_analysis_viewed',
    props: { feature_key: 'ai_overall_analysis' },
  },
  {
    pattern: /^\/agency\/analysis\/youtube(\/|$)/,
    event: 'agency_analysis_viewed',
    props: { feature_key: 'youtube_analysis' },
  },
];

let initialized = false;

function canInitialize() {
  return Boolean(enabledMeta && apiKey);
}

function init() {
  if (!canInitialize() || initialized) {
    return;
  }

  posthog.init(apiKey, {
    api_host: apiHost,
    autocapture: false,
    capture_pageview: true,
    capture_pageleave: true,
    disable_session_recording: true,
    person_profiles: 'identified_only',
    property_denylist: URL_PROPERTY_DENYLIST,
    before_send: beforeSend,
  });

  const distinctId = document
    .querySelector("meta[name='posthog-distinct-id']")
    ?.getAttribute('content');
  const userType = document
    .querySelector("meta[name='posthog-user-type']")
    ?.getAttribute('content');

  if (distinctId) {
    const properties = {};
    if (userType) {
      properties.user_type = userType;
    }
    posthog.identify(distinctId, properties);
  }

  initialized = true;
}

function capture(event, properties = {}) {
  if (!initialized) {
    return;
  }

  posthog.capture(event, properties);
}

function reset() {
  if (!initialized) {
    return;
  }

  posthog.reset();
}

function bindLogoutReset() {
  document.querySelectorAll('[data-posthog-reset]').forEach((element) => {
    element.addEventListener('click', () => {
      reset();
    });
  });
}

function hasCapturedView(pathname) {
  try {
    return sessionStorage.getItem(VIEW_DEDUP_PREFIX + pathname) === '1';
  } catch {
    return false;
  }
}

function markViewCaptured(pathname) {
  try {
    sessionStorage.setItem(VIEW_DEDUP_PREFIX + pathname, '1');
  } catch {
    // sessionStorage unavailable — skip dedupe persistence
  }
}

function capturePageViews() {
  if (!initialized) {
    return;
  }

  const pathname = window.location.pathname;
  if (hasCapturedView(pathname)) {
    return;
  }

  const rule = VIEW_RULES.find((candidate) => candidate.pattern.test(pathname));
  if (!rule) {
    return;
  }

  capture(rule.event, rule.props ?? {});
  markViewCaptured(pathname);
}

function bindIntegrationStarted() {
  document.querySelectorAll('[data-posthog-integration]').forEach((element) => {
    element.addEventListener('click', () => {
      const integrationType = element.getAttribute('data-posthog-integration');
      if (!integrationType) {
        return;
      }

      capture('integration_started', {
        integration_type: integrationType,
      });
    });
  });
}

init();
bindLogoutReset();
capturePageViews();
bindIntegrationStarted();

window.BuzzSolPostHog = {
  capture,
  reset,
  isEnabled: () => initialized,
};
